import clsx from "clsx";
import {
  ActionsMenu,
  ClassificationIcon,
  FileSize,
  Icon,
  NoData,
  SampleDetailsLink,
  ThreatStatusLabel
} from "../../../../../components/";
import { SamplesGridColumnSubtext } from "../../../../search/schema/components/";
import { ActionsModalNames } from "../../../../../components/ActionsModal";
import { consts, enums, settings } from "../../../../../shim/jsbridge";
import { isEmpty } from "../../../util";
import { SUBSCRIBE, UNSUBSCRIBE } from "../consts";
import { convertGoodwareClassification } from "../RlCloudSandboxUtil";
import {
  createApplyTagsAction,
  createBulkSubscriptionAction,
  createSingleSubscriptionAction
} from "./actions-util";
import { copyToUploads } from "../../../../search/services";
import { getSampleDisplayNameFromSchemaData } from "../../../../../utils";

const { FEATURE_ALERTFEED } = settings || {};
const { bulk_download_max_files } = consts || {};
const {
  state: { PROCESSED, ERROR }
} = enums || {};

const RlCloudSandboxDroppedFilesGridSchema = (
  isAllRowsSelected,
  selectedRows,
  hash,
  onToggleModal
) => ({
  fields: [
    {
      name: "_type",
      displayTitle: (
        <span className="icon-upload" style={{ color: "#c4c4c4" }} />
      ),
      sortable: true,
      component: ({ sample }) => (sample ? <Icon name="format" /> : <NoData />),
      className: "samples-grid__icon-cell",
      style: {
        width: "3%",
        textAlign: "center"
      }
    },
    {
      name: "status",
      displayTitle: <ClassificationIcon />,
      sortable: true,
      component: item => {
        const [, classificationIcon] = convertGoodwareClassification(
          item.sample?.threat_status ?? item.classification
        );

        if (item.processing_status === ERROR) {
          return <div className="threat threat--big threat--error" />;
        }

        return (
          <ClassificationIcon
            classification={classificationIcon}
            riskScore={item.threat_factor}
          />
        );
      },
      className: "threat-table-cell",
      style: {
        width: "5%",
        textAlign: "center",
        paddingRight: "15px",
        paddingLeft: "5px"
      }
    },
    {
      name: "threat_name",
      displayTitle: "Threat Name",
      sortable: true,
      component: item => (
        <span title={item.sample?.threat_name}>
          {item.sample?.threat_name || <NoData />}
        </span>
      ),
      className: "overflow-ellipsis",
      style: {
        width: "15%"
      }
    },
    {
      name: "file_name",
      displayTitle: "Name",
      sortable: true,
      component: item => {
        if (item.sample && item.processing_status !== ERROR)
          return (
            <>
              <div className={clsx("samples-grid__name-container")}>
                <SampleDetailsLink
                  sampleRecord={{
                    filename: item.file_name,
                    sha1: item.sha1,
                    threat_status: item.sample?.threat_status,
                    dropped_source_id: hash
                  }}
                  showTitle
                />
              </div>
              <SamplesGridColumnSubtext text={item.sha1} />
            </>
          );

        const [classificationLabel] = convertGoodwareClassification(
          item.classification
        );
        return (
          <>
            <div
              className={clsx(
                "samples-grid__name-container",
                "overflow-ellipsis"
              )}
            >
              <ThreatStatusLabel
                showOrb={false}
                showColor
                showTitle
                threatStatus={classificationLabel}
                labelText={item.file_name || item.sha1}
                className="overflow-ellipsis"
              />
            </div>
            <SamplesGridColumnSubtext text={item.sha1} />
          </>
        );
      },
      className: "overflow-ellipsis",
      style: {
        width: "25%"
      }
    },
    {
      name: "file_path",
      displayTitle: "Path",
      sortable: true,
      className: "overflow-ellipsis",
      style: {
        width: "25%"
      },
      component: item => (
        <span title={item.file_path}>{item.file_path || <NoData />}</span>
      )
    },
    {
      name: "sample_type",
      displayTitle: "Format",
      sortable: true,
      className: "overflow-ellipsis",
      style: {
        width: "10%"
      },
      component: item => item.sample_type || <NoData />
    },
    {
      name: "file_count",
      displayTitle: "Files",
      sortable: true,
      className: "numeric-cell",
      style: {
        width: "5%",
        textAlign: "center"
      },
      component: item => (item.sample ? item.sample?.files_count : <NoData />)
    },
    {
      name: "sample_size",
      displayTitle: "Size",
      sortable: true,
      className: "numeric-cell",
      style: {
        width: "7%",
        textAlign: "center"
      },
      component: item => <FileSize value={item?.sample_size} />
    },
    {
      name: "_actions",
      displayTitle: () => {
        if (!isAllRowsSelected && isEmpty(selectedRows)) {
          return null;
        }
        const selected = Object.values(selectedRows);
        const hasArchives = selected.some(
          row => (row.sample?.files_count || 0) > 1
        );

        const extractedFilesTotalCount = selected.reduce(
          (total, row) => total + (row.sample?.file_count || 0),
          0
        );

        const isExtractedFileCountOverLimit =
          extractedFilesTotalCount > bulk_download_max_files;

        const actions = [
          {
            label: "Reanalyze",
            onClick: () => {
              onToggleModal({
                show: true,
                name: ActionsModalNames.Reanalyze,
                modalData: {
                  samples: selected.map(row => ({
                    hash_value: row.sha1
                  }))
                }
              });
            }
          },
          {
            label: "Download Samples",
            onClick: () => {
              const sampleDownloadData = selected.map(item => ({
                hash: item.sha1,
                filename: item.file_name || item.sha1
              }));
              onToggleModal({
                show: true,
                name: ActionsModalNames.DownloadBulk,
                modalData: { samples: sampleDownloadData }
              });
            }
          },
          {
            label: "Download Extracted Files",
            disabled: !hasArchives || isExtractedFileCountOverLimit,
            disabledMessage:
              (!hasArchives &&
                "Action disabled since none of selected samples contains extracted files.") ||
              (isExtractedFileCountOverLimit &&
                `Total extracted file count over limit (${bulk_download_max_files}).`) ||
              null,
            onClick: () => {
              const sampleDownloadData = selected.map(item => ({
                hash: item.sha1,
                filename: item.file_name || item.sha1
              }));
              onToggleModal({
                show: true,
                name: ActionsModalNames.DownloadBulkUnpacked,
                modalData: { samples: sampleDownloadData }
              });
            }
          }
        ];

        actions.push(createApplyTagsAction(selected, onToggleModal));

        if (FEATURE_ALERTFEED) {
          actions.push(
            createBulkSubscriptionAction(
              SUBSCRIBE,
              ActionsModalNames.Subscribehashes,
              onToggleModal,
              selected
            )
          );
          actions.push(
            createBulkSubscriptionAction(
              UNSUBSCRIBE,
              ActionsModalNames.Unsubscribehashes,
              onToggleModal,
              selected
            )
          );
        }

        return (
          <ActionsMenu
            id="dropped-files-bulk-actions"
            actions={actions}
            className="highlight samples-grid__actions-column"
            stopPropagationOnClick={false}
            sendSkipRowClickEvent
          />
        );
      },
      style: {
        width: "5%"
      },
      component: (item, index) => {
        if (!item.sample) {
          return undefined;
        }

        const hasExtractedFiles = item.sample.files_count > 1;
        const isExtractedFileCountOverLimit =
          item.sample?.files_count > bulk_download_max_files;

        const actions = [
          {
            label: "Reanalyze",
            onClick: () => {
              const { sha1 } = item || {};
              onToggleModal({
                show: true,
                name: ActionsModalNames.Reanalyze,
                modalData: {
                  samples: [
                    {
                      hash_value: sha1
                    }
                  ]
                }
              });
            }
          },
          {
            label: "Copy To My Submissions",
            onClick: () =>
              copyToUploads(
                item?.sample?.sha1,
                getSampleDisplayNameFromSchemaData(item?.sample)
              )
          },
          {
            label: "Download sample",
            onClick: () =>
              onToggleModal({
                show: true,
                modalData: item,
                name: ActionsModalNames.Download
              })
          },
          {
            label: "Set classification",
            onClick: () =>
              onToggleModal({
                show: true,
                modalData: item,
                name: ActionsModalNames.UserClassification
              })
          },
          {
            label: "Download Extracted Files",
            disabled: !hasExtractedFiles || isExtractedFileCountOverLimit,
            disabledMessage:
              (!hasExtractedFiles &&
                "Action disabled since sample doesn't contain extracted files.") ||
              (isExtractedFileCountOverLimit &&
                `Total extracted file count over limit (${bulk_download_max_files}).`) ||
              null,
            onClick: () => {
              onToggleModal({
                show: true,
                name: ActionsModalNames.DownloadUnpacked,
                modalData: {
                  sha1: item.sha1,
                  file_name: item.file_name || item.sha1
                }
              });
            }
          }
        ];

        if (FEATURE_ALERTFEED) {
          actions.push(
            createSingleSubscriptionAction(
              SUBSCRIBE,
              ActionsModalNames.Subscribehashes,
              onToggleModal,
              item.sample?.sha1
            )
          );

          actions.push(
            createSingleSubscriptionAction(
              UNSUBSCRIBE,
              ActionsModalNames.Unsubscribehashes,
              onToggleModal,
              item.sample?.sha1
            )
          );
        }

        return (
          <ActionsMenu
            className="samples-grid__actions-column"
            id={`dropped-files-actions-${index}`}
            actions={actions}
            disabledActionDefaultMessage="Action unavailable for this sample."
            stopPropagationOnClick={false}
            sendSkipRowClickEvent
          />
        );
      }
    }
  ]
});

export default RlCloudSandboxDroppedFilesGridSchema;
